import React, { useState } from 'react';
import { Inbox, MessageSquare, ClipboardList, CalendarDays, BellRing, Flag, ChevronLeft, ChevronRight, ArrowRight, Check } from 'lucide-react';
import { useStepper } from './useStepper';
import { Controls, Tabs } from './OpsSections';
import { PLANS, applyNow } from './homeData';

const IND = [
  { id: 'hvac', label: 'HVAC', need: 'AC blowing warm air upstairs', appt: 'AC Repair Visit', staff: 'Office Manager', qa: [['Is the system running at all?', 'It runs, but the air is warm.'], ['Is this a home or a business?', 'Our home.']], fields: [['System', 'Central AC'], ['Property Type', 'Single-Family Home']] },
  { id: 'roofing', label: 'Roofing', need: 'Ceiling stain after a storm', appt: 'Roof Inspection', staff: 'Project Coordinator', qa: [['Is water coming inside right now?', 'Not right now, just a stain.'], ['Do you know how old the roof is?', 'Not sure.']], fields: [['Roof Age', null], ['Property Type', 'Two-Story Home']] },
  { id: 'medspa', label: 'Med Spas', need: 'Interested in a skin consultation', appt: 'Skin Consultation', staff: 'Client Coordinator', qa: [['Have you visited us before?', 'No, this would be my first visit.'], ['Which treatment are you interested in?', 'I would like to talk through options.']], fields: [['Client Status', 'First Visit'], ['Treatment Interest', null]] },
  { id: 'dental', label: 'Dental', need: 'Tooth sensitivity, looking for a new dentist', appt: 'New Patient Exam', staff: 'Front Desk', qa: [['Are you in pain right now?', 'Mild sensitivity, not severe.'], ['Will you be using dental insurance?', 'I need to check.']], fields: [['Patient Status', 'New Patient'], ['Insurance', null]] },
  { id: 'property', label: 'Property Management', need: 'Unit heater not working', appt: 'Maintenance Visit', staff: 'Property Manager', qa: [['Which property and unit?', 'Maple Court, unit 4B.'], ['When can maintenance access the unit?', 'I will confirm later.']], fields: [['Property', 'Maple Court · Unit 4B'], ['Access Window', null]] },
  { id: 'professional', label: 'Professional Services', need: 'Wants help with monthly bookkeeping', appt: 'Discovery Consultation', staff: 'Account Lead', qa: [['Which service are you looking for?', 'Monthly bookkeeping.'], ['How large is your company?', 'Prefer to discuss on a call.']], fields: [['Service Interest', 'Monthly Bookkeeping'], ['Company Size', null]] },
];

const SCN = [
  { id: 'new', label: 'New Inquiry', source: 'Website Form', outcome: 'Confirmed Booking', path: 'booking' },
  { id: 'missed', label: 'Missed Call', source: 'Missed Call', outcome: 'Human Handoff', path: 'handoff' },
  { id: 'estimate', label: 'Unconverted Estimate', source: 'Eligible Past Lead (Unconverted Estimate)', outcome: 'Follow-Up Scheduled', path: 'followup' },
];

const STAGES = [
  { t: 'Inquiry Received', icon: Inbox }, { t: 'Need Qualified', icon: MessageSquare }, { t: 'Customer Details Captured', icon: ClipboardList },
  { t: 'Next Action Coordinated', icon: CalendarDays }, { t: 'Team & CRM Updated', icon: BellRing }, { t: 'Outcome Recorded', icon: Flag },
];

const statusAt = (k, sc) => {
  if (k < 2) return 'New';
  if (k < 3) return 'Qualified';
  if (sc.path === 'booking') return k < 5 ? 'Appointment Requested' : 'Confirmed Booking';
  if (sc.path === 'handoff') return k < 5 ? 'Handoff Requested' : 'Handed Off To Team';
  return k < 5 ? 'Follow-Up Pending' : 'Follow-Up Scheduled';
};

const Missing = () => <span className="yv-jy-missing">Requires Follow-Up</span>;

const Record = ({ k, v, sc }) => {
  const rows = [
    ['Name', 'Sample Customer', 0], ['Source', sc.source, 0], ['Need', v.need, 1],
    [v.fields[0][0], v.fields[0][1], 2], [v.fields[1][0], v.fields[1][1], 2], ['Appointment Type', v.appt, 3], ['Status', statusAt(k, sc), 0],
  ];
  return (
    <div className="yv-jy-card" data-testid="journey-record">
      <div className="yv-jy-card-head yv-mono">Customer Record · One Sample Customer</div>
      <dl className="yv-hd-record">{rows.map(([key, val, at]) => (
        <div key={key} className={k >= at ? 'on' : ''}><dt>{key}</dt><dd className="yv-keep-case" data-testid={`journey-field-${key.toLowerCase().replace(/\s+/g, '-')}`}>{k < at ? '—' : val ?? <Missing />}</dd></div>
      ))}</dl>
    </div>
  );
};

const NextAction = ({ v, sc }) => {
  if (sc.path === 'booking') return (
    <div><div className="yv-jy-cal" aria-label="Sample availability">{['Tue 9:00', 'Wed 1:00', 'Thu 10:00'].map((t, i) => <span key={t} className={i === 2 ? 'on' : ''}>{t}{i === 2 ? ' · Requested' : ''}</span>)}</div>
      <div className="yv-hd-bubble out">Thursday at 10:00 is open for your {v.appt.toLowerCase()}. Reply YES to request it.</div></div>);
  if (sc.path === 'handoff') return <div className="yv-jy-note">Customer asked to speak with a person. Call-back assigned to the {v.staff.toLowerCase()} instead of automated booking.</div>;
  return <div className="yv-jy-note">Customer is not ready to book. An approved follow-up message is scheduled for later this week.</div>;
};

const Activity = ({ k, v, sc }) => {
  const log = [`Inquiry logged from ${sc.source}`, 'Qualification answers saved', 'Record updated with captured details', 'Next action set', `${v.staff} notified`, `Outcome: ${statusAt(5, sc)}`].slice(0, k + 1);
  return (
    <div className="yv-jy-card yv-keep-case" data-testid="journey-activity">
      <div className="yv-jy-card-head yv-mono">{STAGES[k].t}</div>
      {k === 0 && <div className="yv-jy-inquiry"><Inbox className="w-4 h-4" aria-hidden="true" /><div><b>{sc.source}</b><span>“{v.need}.”</span></div></div>}
      {k === 1 && <div className="yv-jy-thread">{v.qa.flatMap(([q, a]) => [<div key={q} className="yv-hd-bubble out">{q}</div>, <div key={a} className="yv-hd-bubble in">{a}</div>])}</div>}
      {k === 2 && <p className="yv-jy-p">Captured answers populate the record. Anything not provided stays marked <Missing /> — nothing is guessed.</p>}
      {k === 3 && <NextAction v={v} sc={sc} />}
      {k === 4 && <div className="yv-hd-alert"><BellRing className="w-3.5 h-3.5" aria-hidden="true" /> {v.staff} Notified: {v.appt} · {statusAt(4, sc)}</div>}
      {k === 5 && <div className={`yv-jy-outcome yv-jy-${sc.path}`} data-testid="journey-outcome"><Flag className="w-4 h-4" aria-hidden="true" /><div><b>{sc.outcome}</b><span>{sc.path === 'booking' ? `Availability verified and scheduled by the ${v.staff.toLowerCase()}.` : sc.path === 'handoff' ? `A person from your team takes over the conversation.` : 'The lead stays active with a scheduled follow-up.'}</span></div></div>}
      {k >= 3 && <ul className="yv-jy-log" aria-label="CRM activity log">{log.map((l) => <li key={l}>{l}</li>)}</ul>}
    </div>
  );
};

const PackageScope = () => (
  <div className="yv-jy-scope" data-testid="journey-package-scope">
    <div className="yv-jy-card">
      <span className="yv-label">Core Journey · Included In Every System</span>
      <ol className="yv-jy-pilot">{['Missed call or new lead arrives', 'Missed-call text-back or new-lead follow-up sent', 'Reply logged on the lead', 'Owner notified to take the next step'].map((t, i) => <li key={t}><span className="yv-mono">{i + 1}</span>{t}</li>)}</ol>
    </div>
    <div className="yv-jy-card">
      <span className="yv-label">Added Steps—Based On Selected Package</span>
      {PLANS.map((p) => <div key={p.id} className="yv-jy-pkg"><b>{p.name}</b><span>{p.includes.slice(1, 5).join(' · ')}</span></div>)}
    </div>
  </div>
);

export const CustomerJourney = () => {
  const [ind, setInd] = useState('hvac');
  const [scn, setScn] = useState('new');
  const [view, setView] = useState('journey');
  const v = IND.find((x) => x.id === ind);
  const sc = SCN.find((x) => x.id === scn);
  const s = useStepper(6, `${ind}-${scn}`, { delay: 1600 });
  return (
    <section id="journey" className="yv-section yv-grid-bg" data-testid="journey-section">
      <div className="yv-container">
        <div className="yv-section-head yv-split-head">
          <div><span className="yv-tag yv-tag-light">Illustrative Example</span><h2 className="yv-h2 mt-5">One Inquiry. <span className="yv-blue-ink yv-block">Every Next Step Connected.</span></h2></div>
          <p className="yv-lede">Explore how YOVAI captures an opportunity, applies your business rules, and coordinates the next action across your team.</p>
        </div>
        <div className="yv-jy-controls">
          <Tabs items={IND} sel={ind} onPick={setInd} id="journey-industry" label="Industry" />
          <Tabs items={SCN} sel={scn} onPick={setScn} id="journey-scenario" label="Scenario" />
          <Tabs items={[{ id: 'journey', label: 'Full Journey' }, { id: 'packages', label: 'Package Scope' }]} sel={view} onPick={setView} id="journey-view" label="View" />
        </div>
        {view === 'packages' ? <PackageScope /> : (
          <div className="yv-jy" data-testid="journey-demo">
            <ol className="yv-jy-nav" aria-label="Journey stages">
              {STAGES.map((st, k) => { const I = st.icon; return (
                <li key={st.t}><button onClick={() => s.go(k)} aria-current={k === s.i ? 'step' : undefined} className={k < s.i ? 'done' : k === s.i ? 'on' : ''} data-testid={`journey-stage-${k}`}>
                  <span className="yv-jy-dot">{k < s.i ? <Check className="w-3.5 h-3.5" /> : <I className="w-3.5 h-3.5" />}</span>{st.t}</button></li>); })}
            </ol>
            <div className="yv-jy-main">
              <div className="yv-jy-bar"><span className="yv-mono">{v.label} · {sc.label} · Stage {s.i + 1} Of 6</span><Controls s={s} label="journey" /></div>
              <div className="yv-jy-panels" aria-live="polite"><Activity k={s.i} v={v} sc={sc} /><Record k={s.i} v={v} sc={sc} /></div>
              <div className="yv-jy-step">
                <button className="yv-hd-btn" onClick={() => s.go(s.i - 1)} disabled={s.i === 0} data-testid="journey-prev"><ChevronLeft className="w-3.5 h-3.5" />Previous</button>
                <button className="yv-hd-btn" onClick={() => s.go(s.i + 1)} disabled={s.i === 5} data-testid="journey-next">Next<ChevronRight className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        )}
        <button onClick={() => applyNow()} className="yv-btn yv-btn-primary mt-8" data-testid="journey-apply-btn">Apply For A Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" /></button>
      </div>
    </section>
  );
};
